// Pricing.jsx now uses goToCheckout from config.js (via useGeoCheckout) which
// dispatches an event to open the embedded Cashfree modal.
// However we now do a DIRECT REDIRECT to Cashfree's hosted checkout — single
// email field, then off you go. The thank-you page polls for the webhook.
import { CONFIG, trackEvent } from "../../config";
import { ArrowUpRight, Check, ShieldCheck, Loader2 } from "lucide-react";
import { useI18n } from "../../i18n";
import { useGeoCheckout } from "../../hooks/useGeoCheckout";
import { CURRENCIES, fmt } from "../../i18n/currency";

export default function Pricing() {
  const { t } = useI18n();
  const { geo, isIndia, currency: ccyCode, amount } = useGeoCheckout();
  const ccy = CURRENCIES[ccyCode] || CURRENCIES.USD;
  const fmtCcy = (n) => fmt(n, ccyCode);
  const onBuy = () => {
    trackEvent("initiate_checkout", { currency: ccyCode, value: amount, content_ids: ["PPG-EA-V123"], content_type: "product" });

    if (isIndia) {
      window.dispatchEvent(new CustomEvent("dppg:open-cashfree", {
        detail: { currency: ccyCode, amount }
      }));
      return;
    }

  };
  return (
    <section id="pricing" className="relative py-16 sm:py-20 lg:py-28">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="chip">{t("pricing_chip")}</span>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl mt-5 leading-[1.05]">
            {t("pricing_h1_a")} <span className="text-[color:var(--gold)]">{t("pricing_h1_b")}</span>
          </h2>
          <p className="text-[color:var(--ink-dim)] mt-4 text-sm sm:text-base">{t("pricing_sub")}</p>
        </div>

        <div className="mt-10 sm:mt-12 panel p-5 sm:p-8 lg:p-10 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[color:var(--gold)] opacity-[0.08] blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-[color:var(--green)] opacity-[0.06] blur-3xl" />

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-10 relative">
            <div>
              <span className="chip">
                <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--green)] pulse-dot" />
                {t("pricing_offer", { n: CONFIG.COPIES_LEFT })}
              </span>
              <div className="mt-5 sm:mt-6">
                <div className="font-display font-bold text-lg sm:text-xl">{CONFIG.PRODUCT_NAME}</div>
                <div className="font-mono text-[10px] sm:text-[11px] uppercase tracking-widest text-[color:var(--ink-mute)] mt-1">XAUUSD · MT5 · v1.23</div>
              </div>

              <div className="mt-6 sm:mt-8">
                <div className="flex items-baseline gap-3 flex-wrap">
                  <span data-testid="pricing-amount" className="font-display font-black text-5xl sm:text-6xl lg:text-7xl text-[color:var(--gold)] glow-gold">{fmtCcy(ccy.price)}</span>
                  <span className="text-[color:var(--ink-mute)] text-xl sm:text-2xl line-through">{fmtCcy(ccy.mrp)}</span>
                </div>
                <div className="font-mono text-xs uppercase tracking-widest text-[color:var(--green)] mt-2">
                  {t("pricing_save", { amount: fmtCcy(ccy.mrp - ccy.price) })}
                </div>
              </div>

              <button onClick={onBuy} disabled={!geo} data-testid="pricing-cta-buy"
                className="mt-6 sm:mt-8 btn-gold h-12 px-5 sm:px-6 rounded-full inline-flex items-center gap-2 text-sm font-display disabled:opacity-60">
                {!geo && <Loader2 className="w-4 h-4 animate-spin" />}
                {t("pricing_cta")} <ArrowUpRight className="w-4 h-4" />
              </button>

              <div className="mt-4 sm:mt-5 flex items-center gap-2 text-[11px] sm:text-xs text-[color:var(--ink-mute)]">
                <ShieldCheck className="w-4 h-4 text-[color:var(--gold)]" />
                {isIndia ? "Cashfree · UPI / Cards / Netbanking · Direct redirect to secure checkout" : t("pricing_secure")}
              </div>
            </div>

            <div className="border-t lg:border-t-0 lg:border-l border-dashed border-[#23232a] pt-6 lg:pt-0 pl-0 lg:pl-10">
              <div className="font-mono text-[10px] sm:text-[11px] uppercase tracking-widest text-[color:var(--ink-mute)] mb-4">{t("pricing_what_get")}</div>
              <ul className="space-y-3">
                {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                  <li key={i} className="flex gap-3">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-[rgba(0,255,136,.1)] grid place-items-center shrink-0">
                      <Check className="w-3 h-3 text-[color:var(--green)]" />
                    </span>
                    <span className="text-[13px] sm:text-sm text-[color:var(--ink-dim)]">{t(`pricing_inc${i}`)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
